/**
 * Tabla de personas de un roster (admins, viewers, hiring managers…): nombre y
 * mail, rol y área del community map, y "quitar" (o candado si es fijo).
 * Vivía adentro de GovernancePanel; desde el 28/09/2026 la comparten
 * GovernancePanel y las cards que administran otra lista de personas (Hiring
 * Managers de TA). `namespace` = claves i18n col_name / col_role / col_area /
 * remove / fixed_admin / not_in_map de la sección.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export interface RosterMember {
  email: string;
  name: string;
  role?: string;
  area?: string;
  team?: string;
  status?: string;
}

export function RosterTable({ emails, members, namespace, locked, empty, onRemove }: {
  emails: string[];
  members: RosterMember[];
  namespace: string;
  /** Fila con candado (no se puede quitar), p. ej. el admin fijo de la sección. */
  locked?: (email: string) => boolean;
  empty: string;
  onRemove: (email: string) => void;
}) {
  const { t } = useTranslation();
  const k = namespace;
  const memberByEmail = useMemo(() => new Map(members.map((m) => [m.email.toLowerCase(), m] as const)), [members]);

  if (emails.length === 0) return <p className="py-6 text-center text-sm text-muted-foreground">{empty}</p>;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t(`${k}.col_name`)}</TableHead>
          <TableHead>{t(`${k}.col_role`)}</TableHead>
          <TableHead>{t(`${k}.col_area`)}</TableHead>
          <TableHead className="w-[48px]" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {emails.map((email) => {
          const m = memberByEmail.get(email.toLowerCase());
          const inactive = m && m.status && m.status !== 'Activo';
          const isLocked = locked?.(email) ?? false;
          return (
            <TableRow key={email}>
              <TableCell className="font-medium">
                {m?.name || email}
                {!m && <span className="ml-2 text-xs text-amber-600">{t(`${k}.not_in_map`)}</span>}
                {inactive && <span className="ml-2 text-xs text-amber-600">({m!.status})</span>}
                <div className="text-xs text-muted-foreground">{email}</div>
              </TableCell>
              <TableCell className="text-muted-foreground">{m?.role || '—'}</TableCell>
              <TableCell className="text-muted-foreground">{m?.area || m?.team || '—'}</TableCell>
              <TableCell>
                {isLocked ? (
                  <span className="inline-flex h-8 w-8 items-center justify-center text-muted-foreground/70" title={t(`${k}.fixed_admin`)}><Lock className="h-4 w-4" /></span>
                ) : (
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => onRemove(email)} aria-label={t(`${k}.remove`)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
